import { Coordinate } from "~/generated/graphql.types";
import { SpotForRoutingFragment } from "~/routes/_shared/fragments.generated";

const calculateNearestCoordinateOnLine = ({
  inputCoordinate,
  line,
}: {
  inputCoordinate: Coordinate;
  line: [Coordinate, Coordinate];
}) => {
  // lineの始点を原点に移動する。
  const moveOriginLine = [
    { lon: 0, lat: 0 },
    {
      lon: Number(line[1].lon) - Number(line[0].lon),
      lat: Number(line[1].lat) - Number(line[0].lat),
    },
  ];
  // inputCoordinateをその移動に合わせて移動.
  const moveOriginCoordinate = {
    lon: Number(inputCoordinate.lon) - Number(line[0].lon),
    lat: Number(inputCoordinate.lat) - Number(line[0].lat),
  };

  // lineとx軸(plus方向)のなす角を求める。(sin cosの値を求める)
  const cos =
    (moveOriginLine[1].lat * 1 + moveOriginLine[1].lon * 0) /
    (Math.sqrt(moveOriginLine[1].lat ** 2 + moveOriginLine[1].lon ** 2) *
      Math.sqrt(1 ** 2 + 0 ** 2));
  const sin = (moveOriginLine[1].lon < 0 ? -1 : 1) * Math.sqrt(1 - cos ** 2);
  // lineの原点ではない方をx軸に載せた線を生成する.(lineの長さを計算して、[0, その長さ]でも取得できるが、検算のため。。。)

  const rotateLine = [
    { lon: 0, lat: 0 },
    {
      lon: moveOriginLine[1].lon * cos - moveOriginLine[1].lat * sin,
      lat: moveOriginLine[1].lat * cos + moveOriginLine[1].lon * sin,
    },
  ];

  // inputCoordinateをその回転に合わせて回転.
  const rotateCoordinate = {
    lon: moveOriginCoordinate.lon * cos - moveOriginCoordinate.lat * sin,
    lat: moveOriginCoordinate.lat * cos + moveOriginCoordinate.lon * sin,
  };
  // 回転したlineとinputCoordinateの距離を求める。
  // rotateCoordinate の latが 0 より小さい場合は、最も近い点はlineの始点(原点)となる。
  // そのため、rootCoordinateの2乗和の平方根がそのまま距離となる。
  if (rotateCoordinate.lat < 0) {
    return {
      distance: Math.sqrt(
        rotateCoordinate.lat ** 2 + rotateCoordinate.lon ** 2
      ),
      nearestCoordinate: { lon: line[0].lon, lat: line[0].lat },
    };
  }
  // rotateCoordinate の latが lineの終点のlat より大きい場合は、最も近い点はlineの終点となる。
  if (rotateCoordinate.lat > rotateLine[1].lat) {
    return {
      distance: Math.sqrt(
        (rotateCoordinate.lat - rotateLine[1].lat) ** 2 +
          rotateCoordinate.lon ** 2
      ),
      nearestCoordinate: { lon: line[1].lon, lat: line[1].lat },
    };
  }
  // それ以外の場合は、rotateCoordinateのlonの値がそのまま距離となる。
  // その場合の場所を求める。
  // 以下は回転後の一番近いcoordinate.
  const rotatedNeaerstCoordinate = {
    lon: 0,
    lat: rotateCoordinate.lat,
  };
  // 移動後回転前の一番近いcoordinate.
  const movedNearestCoordinate = {
    lon:
      rotatedNeaerstCoordinate.lon * cos - rotatedNeaerstCoordinate.lat * -sin,
    lat:
      rotatedNeaerstCoordinate.lat * cos + rotatedNeaerstCoordinate.lon * -sin,
  };
  // 移動前の一番近いcoordinate.
  const nearestCoordinate = {
    lon: (movedNearestCoordinate.lon + Number(line[0].lon)).toString(),
    lat: (movedNearestCoordinate.lat + Number(line[0].lat)).toString(),
  };
  return { distance: Math.abs(rotateCoordinate.lon), nearestCoordinate };
};

export const getNearestCoordinateOrder = ({
  sourceCoordinate,
  targetCoordinates,
}: {
  sourceCoordinate: Coordinate;
  targetCoordinates: Coordinate[];
}) => {
  if (sourceCoordinate === undefined) {
    return undefined;
  }
  if (targetCoordinates.length < 2) {
    return undefined;
  }
  // 全てのスポットとクリックしたスポットの距離を計算し、最小となる点を返す。(正確な距離ではなくLat Lonの差の絶対値の二乗和の平方根)
  let minDistIndex = -1;
  let minDist = 10 ** 10;
  for (let i = 0; i < targetCoordinates.length - 1; i++) {
    const dist = calculateNearestCoordinateOnLine({
      inputCoordinate: sourceCoordinate,
      line: [targetCoordinates[i], targetCoordinates[i + 1]],
    }).distance;
    if (dist < minDist) {
      minDist = dist;
      minDistIndex = i;
    }
  }
  return minDistIndex;
};

export const getNearestCoordinateOnRouting = ({
  targetCoordinate,
  coordinatesToRouting,
}: {
  targetCoordinate: Coordinate;
  coordinatesToRouting: Coordinate[];
}) => {
  //Routingに対して、最も近い点とその距離を求める。
  let minDist = 10 ** 10;
  let nearestCoordinates;
  let nearestEndPoints: Coordinate[];
  for (let i = 0; i < coordinatesToRouting.length - 2; i++) {
    const tempNearestCoordinate = calculateNearestCoordinateOnLine({
      inputCoordinate: targetCoordinate,
      line: [coordinatesToRouting[i], coordinatesToRouting[i + 1]],
    });
    if (tempNearestCoordinate.distance < minDist) {
      minDist = tempNearestCoordinate.distance;
      nearestCoordinates = tempNearestCoordinate.nearestCoordinate;
      nearestEndPoints = [coordinatesToRouting[i], coordinatesToRouting[i + 1]];
    }
  }
  return minDist;
};
